// External Imports
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import LoadingSpinner from "./LoadingSpinner";
import { MdAdd, MdClose, MdTag } from "react-icons/md";
import { requestAdminAPI } from "../../api/posh-api";
import { UtilityContext } from "../../context/util-context";

// Internal Imports

/** ========================================================================
 * * TagSelector Component
 * This component renders a dropdown to select a tag.
 * ========================================================================== */

export default function TagSelector({ table, id }) {
  const { navigate } = useContext(UtilityContext);

  // Initialize States
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [tags, setTags] = useState([]);
  const [show, setShow] = useState(false);
  const [color, setColor] = useState("#ca8a04");

  // Declare Refs
  const inputRef = useRef(null);

  /* ====================== FETCH RECORDS  ====================== */
  const fetchTags = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("filter", {
        table: `${table}Tag`,
        filters: [`${table}Id: { eq: "${id}" }`],
        columns: ["tag { tag color }"],
      });
      setTags(response.map((item) => ({ ...item.tag, id: item.id })));
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ====================== FETCH RECORDS  ====================== */
  const fetchRecords = async (e) => {
    e.preventDefault();
    const query = e.target.query.value;
    if (!query) return;
    try {
      setLoading(true);
      const response = await requestAdminAPI("filter", {
        table: "tag",
        filters: [`tag: { contains: "${query}" }`],
      });
      setRecords(response);
      setShow(true);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= ADD RECORD ======================= */
  const addRecord = async (tag) => {
    try {
      setLoading(true);
      await requestAdminAPI("add-tag", {
        tag: tag,
        table,
        id,
        color,
      });
      inputRef.current.value = "";
      setLoading(false);
      fetchTags();
      setShow(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= REMOVE TAG ======================= */
  const removeTag = async (tagId) => {
    try {
      setLoading(true);
      await requestAdminAPI("remove-tag", {
        table,
        tagId: tagId,
      });
      fetchTags();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) fetchTags();
  }, [id]);

  return (
    <Fragment>
      {show && (
        <div
          onClick={() => setShow(false)}
          className="absolute top-0 left-0 w-full h-full -z-1 no-scrollbar"
        ></div>
      )}
      <div className="relative grid gap-2 text-sm">
        <form onSubmit={fetchRecords} className="">
          <div className="flex items-center border rounded-md border-stone-600 ">
            <input
              className="relative w-full text-sm input"
              autoComplete="off"
              name="query"
              type="text"
              autoCorrect="off"
              spellCheck="false"
              ref={inputRef}
              placeholder="Add Tag..."
            />
            <button type="submit" className="px-2">
              {loading ? (
                <LoadingSpinner classes="text-primary w-5 h-5" />
              ) : (
                <MdTag className="w-5 h-5 icon" />
              )}
            </button>
          </div>
        </form>
        {loading ? (
          <div className="grid gap-2">
            <div className="w-full h-5 rounded-md bg-stone-800 animate-pulse"></div>
            <div className="w-full h-5 rounded-md bg-stone-800 animate-pulse"></div>
          </div>
        ) : (
          <div className="grid gap-2">
            {tags.map((tag, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <div
                  onClick={(e) => navigate(`/${table}s?query=${tag.tag}`, e)}
                  className="flex items-center space-x-2 cursor-pointer hover:opacity-80"
                >
                  <MdTag
                    className="w-5 h-5"
                    style={{
                      color: tag.color,
                    }}
                  />
                  <p
                    style={{
                      color: tag.color,
                    }}
                    key={idx}
                  >
                    {tag.tag}
                  </p>
                </div>
                <MdClose
                  className="mr-2 text-xl icon"
                  onClick={() => removeTag(tag.id)}
                />
              </div>
            ))}
          </div>
        )}
        {show && (
          <div className="absolute top-12 py-2 px-4 w-full overflow-scroll rounded-lg bg-stone-700 no-scrollbar max-h-[300px] z-10">
            {records.map((tag) => (
              <div
                key={tag.id}
                className="px-2 py-1 text-sm font-semibold rounded-md cursor-pointer text-stone-200 hover:bg-stone-600"
                onClick={() => addRecord(tag.tag)}
              >
                {tag.tag}
              </div>
            ))}

            <div className="flex items-center justify-between px-2 py-1 text-sm rounded-md text-stone-200">
              <div className="grid gap-2">
                <p className="font-semibold">
                  "{inputRef.current?.value}"{" "}
                  <span className="font-normal text-stone-400">
                    - add as a new tag.
                  </span>
                </p>
                <div className="grid grid-cols-5 cursor-pointer justify-items-center">
                  <div
                    className={`w-4 h-4 bg-[#ca8a04] rounded-full ${
                      color === "#ca8a04" ? "border border-white" : ""
                    }`}
                    onClick={() => setColor("#ca8a04")}
                  ></div>
                  <div
                    className={`w-4 h-4 bg-[#16a34a] rounded-full ${
                      color === "#16a34a" ? "border border-white" : ""
                    }`}
                    onClick={() => setColor("#16a34a")}
                  ></div>
                  <div
                    className={`w-4 h-4 bg-[#0891b2] rounded-full ${
                      color === "#0891b2" ? "border border-white" : ""
                    }`}
                    onClick={() => setColor("#0891b2")}
                  ></div>
                  <div
                    className={`w-4 h-4 bg-[#7c3aed] rounded-full ${
                      color === "#7c3aed" ? "border border-white" : ""
                    }`}
                    onClick={() => setColor("#7c3aed")}
                  ></div>
                  <div
                    className={`w-4 h-4 bg-[#dc2626] rounded-full ${
                      color === "#dc2626" ? "border border-white" : ""
                    }`}
                    onClick={() => setColor("#dc2626")}
                  ></div>
                </div>
              </div>
              <MdAdd
                className="icon"
                onClick={() => addRecord(inputRef.current?.value)}
              />
            </div>
          </div>
        )}
      </div>
    </Fragment>
  );
}
